import type { Entry } from '../../features/admin/modules/modules.model';
import { buildRoleTokenSet, templateAppliesToRoleTokens, type RoleLike } from './document-template-roles';

/**
 * Which document templates the onboarding checklist shows, in one place.
 *
 * The carrier-facing checklist (`required-documents-checklist.component.ts`), the reviewer's
 * document list (`readonly-onboarding.component.ts`) and the approve/reject fan-out
 * (`ReviewDecisionCoordinator`) must agree on this set. When the review side accepted any
 * document with a `template-source`, Goedkeuren also approved pending documents from templates
 * the checklist hides (`showInOnboarding` off, another target role) — records nobody assessed as
 * part of this onboarding.
 */

export const ONBOARDING_TEMPLATE_MODULE_CODE = 'document-templates';
export const ONBOARDING_TEMPLATE_FILTER = "entry.archived eq false and entry.showInOnboarding eq 'true'";

/** Roles an onboarding leads to, declared on the onboarding-forms entry (`entry.settings.roles`). */
export function onboardingTargetRoleIds(onboardingForm: Entry | null | undefined): string[] {
  const roles = onboardingForm?.entry?.['settings']?.['roles'];
  return Array.isArray(roles) ? roles.filter((role): role is string => typeof role === 'string' && !!role) : [];
}

/**
 * No target roles configured means no role filtering — NOT "match nothing": an empty token set
 * would reject every template, while "no target roles" is meant to apply to everyone.
 */
export function templateMatchesTargetRoles(
  template: Entry,
  targetRoleIds: readonly string[],
  allRoles: readonly RoleLike[],
  slugify: (text: string) => string | null
): boolean {
  if (targetRoleIds.length === 0) return true;
  return templateAppliesToRoleTokens(
    template.entry?.['appliesToRoles'],
    buildRoleTokenSet(targetRoleIds, allRoles, slugify)
  );
}

/**
 * A document belongs to an onboarding review when it is this user's or this company's AND it was
 * uploaded against a template the checklist shows. A free-form upload (no `template-source`) is
 * never part of it.
 */
/** Quotes a value for a single-quoted OData string literal. */
export function escapeODataString(value: string): string {
  return value.replaceAll("'", "''");
}

/**
 * The server-side half of {@link documentBelongsToReview}: non-archived records anchored to the user
 * OR the company. The template rule cannot be expressed in the filter and is applied client-side.
 */
export function reviewDocumentsFilter(userId: string, companyId: string | null): string {
  const userClause = `entry.user.id eq '${escapeODataString(userId)}'`;
  const anchor = companyId ? `(entry.company.id eq '${escapeODataString(companyId)}' or ${userClause})` : userClause;
  return `${anchor} and entry.archived eq false`;
}

export function documentBelongsToReview(
  document: Entry,
  userId: string,
  companyId: string | null,
  visibleTemplateIds: ReadonlySet<string>
): boolean {
  const templateId = document.entry?.['template-source']?.id;
  if (typeof templateId !== 'string' || !visibleTemplateIds.has(templateId)) return false;
  if (document.entry?.['user']?.id === userId) return true;
  return companyId !== null && document.entry?.['company']?.id === companyId;
}
